import { useEffect, useRef, RefObject } from 'react';
import Vivus from "vivus";

/**
 * Loads an SVG from a specified URL and returns its content as a string
 * @param url URL of the SVG file to load
 * @returns Promise that resolves to the SVG content as a string
 */
export async function loadSvgFromUrl(url: string): Promise<string> {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to load SVG from ${url}: ${response.statusText}`);
        }
        return await response.text();
    } catch (error) {
        console.error('Error loading SVG:', error);
        return '';
    }
}

/**
 * Set basic SVG attributes for responsive display
 * @param svgElement SVG element to configure
 * @param height Optional height in px, defaults to window inner height
 */
export function setupResponsiveSvg(
    svgElement: SVGElement,
    height: number = window.innerHeight
): void {
    svgElement.setAttribute('width', '100%');
    svgElement.setAttribute('height', `${height}px`);
    svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');
}

/**
 * Set SVG path styles for all paths or specific paths by IDs
 * @param svgElement SVG element containing paths
 * @param fill Fill color
 * @param stroke Stroke color
 * @param strokeWidth Stroke width
 * @param pathIds Optional array of path IDs to target specific paths
 */
export function setSvgPathStyles(
    svgElement: SVGElement,
    fill: string,
    stroke: string,
    strokeWidth: string = '1',
    pathIds?: string[] | Set<string>
): void {
    if (pathIds) {
        // Check if we have paths to target (either non-empty array or non-empty set)
        const isEmpty = Array.isArray(pathIds) ? pathIds.length === 0 : pathIds.size === 0;

        if (!isEmpty) {
            // Set styles for specific paths
            (Array.isArray(pathIds) ? pathIds : Array.from(pathIds)).forEach(id => {
                const path = (svgElement as SVGSVGElement).getElementById(id);
                if (path) {
                    path.setAttribute('fill', fill);
                    path.setAttribute('stroke', stroke);
                    path.setAttribute('stroke-width', strokeWidth);
                    // Increase stroke-opacity and fill-opacity for better visibility
                    path.setAttribute('stroke-opacity', '1');
                    path.setAttribute('fill-opacity', '1');
                }
            });
            return; // Exit early after applying styles to specific paths
        }
    }

    // If pathIds is undefined, empty array, or empty set, set styles for all paths
    const paths = svgElement.querySelectorAll('path');
    paths.forEach(path => {
        path.setAttribute('fill', fill);
        path.setAttribute('stroke', stroke);
        path.setAttribute('stroke-width', strokeWidth);
        // Increase stroke-opacity and fill-opacity for better visibility
        path.setAttribute('stroke-opacity', '1');
        path.setAttribute('fill-opacity', '1');
    });
}
/**
 * Calculate perceived brightness of a color
 * @param r Red component (0-255)
 * @param g Green component (0-255)
 * @param b Blue component (0-255)
 * @returns Brightness value (0-255)
 */
export function calculateBrightness(r: number, g: number, b: number): number {
    // Formula: (0.299*R + 0.587*G + 0.114*B)
    return 0.299 * r + 0.587 * g + 0.114 * b;
}

/**
 * Determine if a background color is dark
 * @param bgColor CSS color string (rgb or rgba)
 * @param threshold Brightness threshold (0-255)
 * @returns Boolean indicating if the color is dark
 */
export function isDarkBackground(
    bgColor: string,
    threshold: number = 128
): boolean {
    const rgbMatch = bgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (!rgbMatch) return false;

    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);

    return calculateBrightness(r, g, b) < threshold;
}

/**
 * Get the current visible section based on scroll position
 * @param sectionIds Array of section IDs to check
 * @returns The currently most visible section element or null
 */
export function getCurrentVisibleSection(sectionIds: string[]): HTMLElement | null {
    const viewportHeight = window.innerHeight;
    const scrollY = window.scrollY;
    const viewportMiddle = scrollY + viewportHeight / 2;

    let currentSection = null;
    let maxOverlap = 0;

    for (const id of sectionIds) {
        const section = document.getElementById(id);
        if (!section) continue;

        const rect = section.getBoundingClientRect();
        const sectionTop = rect.top + scrollY;
        const sectionBottom = rect.bottom + scrollY;

        // Check if section is in view
        if (sectionTop <= viewportMiddle && sectionBottom >= viewportMiddle) {
            const overlap = Math.min(viewportHeight, sectionBottom - sectionTop);
            if (overlap > maxOverlap) {
                maxOverlap = overlap;
                currentSection = section;
            }
        }
    }

    return currentSection;
}

/**
 * Custom hook to adjust SVG colors based on the background color of visible sections
 * @param containerRef Ref to the container with the SVG
 * @param svgContent SVG content string
 * @param pathIds IDs of specific paths to modify
 * @param sectionIds IDs of sections to check for visibility
 * @param darkModeColors Colors to use on dark backgrounds {fill, stroke}
 * @param lightModeColors Colors to use on light backgrounds {fill, stroke}
 * @param animationDuration Optional duration for Vivus animation
 */
export function useDynamicSvgColors(
    containerRef: RefObject<HTMLDivElement | null>,
    svgContent: string,
    pathIds: string[] | Set<string>,
    sectionIds: string[],
    darkModeColors: { fill: string; stroke: string },
    lightModeColors: { fill: string; stroke: string },
    animationDuration: number = 200
): void {
    const vivusInstanceRef = useRef<any>(null);

    useEffect(() => {
        if (!containerRef.current || !svgContent) return;

        const svgElement = containerRef.current.querySelector('svg');
        if (!svgElement) return;

        // Set up SVG
        setupResponsiveSvg(svgElement as SVGElement);

        // Use a Set to keep track of paths that are being managed by this instance
        const managedPaths = new Set<string>();

        // If pathIds is empty, find all paths that aren't managed by other instances
        if (pathIds instanceof Set && pathIds.size === 0) {
            const allPaths = svgElement.querySelectorAll('path');
            allPaths.forEach(path => {
                if (path.id) {
                    managedPaths.add(path.id);
                }
            });
        } else {
            // Convert pathIds to a Set if it's an array
            const pathIdSet = Array.isArray(pathIds) ? new Set(pathIds) : pathIds;
            pathIdSet.forEach(id => managedPaths.add(id));
        }

        // Update colors based on the current section's data-theme attribute
        const updateColorsBasedOnSection = () => {
            const currentSection = getCurrentVisibleSection(sectionIds);
            if (currentSection) {
                const theme = currentSection.getAttribute('data-theme');
                // Use 'dark' if data-theme equals "dark", otherwise assume light mode
                const isDark = theme === 'dark';
                const { fill, stroke } = isDark ? darkModeColors : lightModeColors;

                // Apply stronger stroke width to make it more visible
                setSvgPathStyles(svgElement as SVGElement, fill, stroke, '1', managedPaths);
            }
        };

        // Set initial colors
        updateColorsBasedOnSection();

        // Add scroll listener for dynamic updates
        window.addEventListener('scroll', updateColorsBasedOnSection);

        // Animation setup (if using Vivus)
        if (typeof Vivus !== 'undefined' && !vivusInstanceRef.current) {
            try {
                vivusInstanceRef.current = new Vivus(svgElement as unknown as HTMLElement, {
                    type: 'sync',
                    duration: animationDuration,
                    start: 'autostart',
                    animTimingFunction: (x: number) => x * x,
                });
            } catch (error) {
                console.error('Error initializing Vivus:', error);
            }
        }

        // Clean up
        return () => {
            window.removeEventListener('scroll', updateColorsBasedOnSection);
            if (vivusInstanceRef.current) {
                // Clean up Vivus instance if needed
                vivusInstanceRef.current = null;
            }
        };
    }, [containerRef, svgContent, pathIds, sectionIds, darkModeColors, lightModeColors, animationDuration]);
}

/**
 * Extract all paths from an SVG element
 * @param svgElement SVG element to extract paths from
 * @returns Array of path objects with id, d attribute, and style
 */
export function extractSvgPaths(svgElement: SVGElement): Array<{ id: string; d: string; style: string }> {
    const pathElements = svgElement.querySelectorAll('path');

    return Array.from(pathElements).map(path => ({
        id: path.id,
        d: path.getAttribute('d') || '',
        style: path.getAttribute('style') || ''
    }));
}